import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withDevtools } from '@ngrx-toolkit/core';
import { Observable, pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ApiService } from 'src/api';
import { Sharing } from 'src/api/models';
import { trackEvent } from 'src/app/core/services/google-analytics';
import { arraysEqual } from 'src/app/shared/utils/common.utils';
import { GoogleAnalyticsEvent } from 'src/app/shared/enums/google-analytics-event.enum';
import { RoutePath } from 'src/app/shared/enums/route-path.enum';
import {
  ShareableEntityModel,
  SharingExpandIndex,
  SharingStateEntityModel,
  SharingTeamModel,
  SharingTreeUserModel,
} from 'src/app/shared/models/sharing.model';
import { SharingType } from 'src/app/features/sharing/enums/sharing-type.enum';
import { ShareableEntityType } from 'src/app/features/sharing/models/shareable-entity-type.model';
import {
  createSharingTree,
  flattenSharingTree,
} from 'src/app/features/sharing/sharing.utils';
import { RouteStore } from './route.store';
import { catchApiError } from './store.operators';
import { withApiOperation } from './features/with-api-operation.feature';
import { withRequestStatus } from './features/with-request-status.feature';

interface SharingMutation {
  add?: { userId?: string; teamId?: string }[];
  del?: { userId?: string; teamId?: string }[];
}

interface SharingState {
  entity: SharingStateEntityModel | null;
}

const initialState: SharingState = {
  entity: null,
};

const isSameShareable = (
  next: ShareableEntityModel,
  prev: ShareableEntityModel,
): boolean => {
  switch (next.type) {
    case ShareableEntityType.LIST:
      return next.annotationListId === prev.annotationListId;
    case ShareableEntityType.PLAYLIST:
      return next.playlistId === prev.playlistId;
    case ShareableEntityType.ANNOTATIONS:
      return (
        !!next.annotationsIds &&
        !!prev.annotationsIds &&
        arraysEqual(next.annotationsIds, prev.annotationsIds)
      );
    default:
      return false;
  }
};

const isTeamSharing = (method: SharingType): boolean =>
  method === SharingType.TEAM;

const sharingMessageKey = (type: ShareableEntityType | undefined): string =>
  type === ShareableEntityType.PLAYLIST ? 'sharePlaylist' : 'shareTag';

export const SharingStore = signalStore(
  withDevtools('sharing'),
  withRequestStatus(),
  withApiOperation(),
  withState(() => initialState),
  withProps(() => ({
    _routeStore: inject(RouteStore),
  })),
  withComputed(store => ({
    isSharingEnabled: computed(
      () => !store._routeStore.path().includes(RoutePath.Playlists),
    ),
    sharingTeamsStateMappedExpand: computed(() => {
      const sharingTeamsEntity = store.entity();
      if (sharingTeamsEntity) {
        return {
          tree: flattenSharingTree(sharingTeamsEntity.tree),
          expanded: sharingTeamsEntity.expanded
            ? sharingTeamsEntity.expanded
            : [],
        };
      }
      return null;
    }),
  })),
  withMethods(store => {
    const apiClient = inject(ApiService);

    const callSharingsApi = (
      shareable: ShareableEntityModel | null | undefined,
      body: SharingMutation,
    ): Observable<Sharing[]> => {
      switch (shareable?.type) {
        case ShareableEntityType.ANNOTATIONS:
          return apiClient.annotationsSharingsPost({
            body: { ids: shareable.annotationsIds ?? [], ...body },
          });
        case ShareableEntityType.PLAYLIST:
          return apiClient.playlistsIdSharingsPost({
            id: shareable.playlistId ?? '',
            body,
          });
        default:
          return apiClient.annotationListsIdSharingsPost({
            id: shareable?.annotationListId ?? '',
            body,
          });
      }
    };

    return {
      get: rxMethod<ShareableEntityModel>(
        pipe(
          tap(() => store.setLoading()),
          switchMap(shareable =>
            apiClient.teamsSharingsGet().pipe(
              tap(sharingTeams =>
                patchState(store, state => ({
                  entity:
                    state.entity &&
                    isSameShareable(shareable, state.entity.shareable)
                      ? state.entity
                      : {
                          tree: createSharingTree(
                            sharingTeams,
                            shareable?.sharings || [],
                          ),
                          expanded: sharingTeams.map(sharingTeam => ({
                            [sharingTeam.id]: {
                              expanded: false,
                            },
                          })),
                          shareable,
                          sharingTeams,
                        },
                })),
              ),
              tap(() => store.setLoaded()),
              catchApiError(store.handleApiError),
            ),
          ),
        ),
      ),
      toggleTree(teamId: string) {
        patchState(store, state => ({
          entity: state.entity
            ? {
                ...state.entity,
                expanded: state.entity.expanded.map(
                  (expandIndex: SharingExpandIndex) => {
                    if (expandIndex[teamId]) {
                      return {
                        [teamId]: { expanded: !expandIndex[teamId].expanded },
                      };
                    }
                    return expandIndex;
                  },
                ),
              }
            : null,
        }));
      },
      async toggleUser(user: SharingTreeUserModel) {
        const shareable = store.entity()?.shareable;
        if (!shareable) {
          return;
        }

        const mutation: SharingMutation = {
          [user.selected ? 'del' : 'add']: [{ userId: user.id }],
        };

        /* Tracked before the call, not as the operation's success `event`:
         * this counts the attempt. */
        trackEvent(
          user.selected
            ? GoogleAnalyticsEvent.REMOVE_SHARE_WITH_USER
            : GoogleAnalyticsEvent.SHARE_WITH_USER,
          {
            type: shareable.type,
          },
        );

        const entityKey = sharingMessageKey(shareable.type);

        await store.runApiOperation({
          request: callSharingsApi(shareable, mutation),
          successSnackbar: {
            message: `success.${entityKey}.user${user.selected ? 'Del' : 'Add'}`,
            entryEnd: user.label,
            icon: 'heroShare',
          },
          errorSnackbar: {
            message: `error.${entityKey}.user${user.selected ? 'Del' : 'Add'}`,
          },
          onSuccess: sharings =>
            patchState(store, state => ({
              entity: state.entity
                ? {
                    ...state.entity,
                    shareable: {
                      ...state.entity.shareable,
                      sharings: [...sharings],
                    },
                    tree: createSharingTree(
                      state.entity.sharingTeams,
                      sharings,
                    ),
                  }
                : null,
              loading: false,
              loaded: true,
            })),
        });
      },
      async changeMethod(team: SharingTeamModel, sharingType: SharingType) {
        const shareable = store.entity()?.shareable;
        if (!shareable) {
          return;
        }

        const teamMembers = team.users ? team.users : [];
        const isSharing = isTeamSharing(sharingType);
        const body: SharingMutation = isSharing
          ? {
              add: [{ teamId: team.id }],
              del: teamMembers.map(teamMember => ({ userId: teamMember.id })),
            }
          : {
              del: [{ teamId: team.id }],
            };

        /* Tracked before the call, not as the operation's success `event`:
         * this counts the attempt. */
        trackEvent(
          isSharing
            ? GoogleAnalyticsEvent.SHARE_WITH_TEAM
            : GoogleAnalyticsEvent.REMOVE_SHARE_WITH_TEAM,
          {
            type: shareable.type,
          },
        );
        const entityKey = sharingMessageKey(shareable.type);

        await store.runApiOperation({
          request: callSharingsApi(shareable, body),
          successSnackbar: {
            message: `success.${entityKey}.changeMode${
              isSharing ? 'Team' : 'Single'
            }`,
            entryEnd: team.label,
            icon: 'heroShare',
          },
          errorSnackbar: {
            message: `error.${entityKey}.changeMode`,
            icon: 'heroXMark',
          },
          onSuccess: sharings =>
            patchState(store, state => ({
              entity: state.entity
                ? {
                    ...state.entity,
                    tree: createSharingTree(
                      state.entity.sharingTeams,
                      sharings,
                    ),
                  }
                : null,
              loading: false,
              loaded: true,
            })),
        });
      },
    };
  }),
);
